/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {createModularSearchQueryString} from '@ion/applications/src/pages/devices/ModularSearchUtils';
import {getFilterValue, url} from '@ion/core/src/services/ConnectUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  APIGatewayMemberSearch,
  CarbExportParams,
  CarbExportSearch,
  CarrierPartNumbersExportSearch,
  ExportSearch,
  InventoryExportSearch,
  JobSearchEngineExportSearch,
  JobsTaskExportSearch,
  Service
} from '@ion/core/src/types/services';
import {FormState} from '../hooks/useFormState';

// Available field names for the Bulk email export form
export enum EmailExportFields {
  EmailAddress = 'email'
}

// Available field names for the Bulk email export custom logic fields.
enum CUSTOM_LOGIC_FIELDS {
  IDENTIFIER = 'identifier',
  ACCOUNT = 'account',
  ASSET = 'asset',
  DEVICE = 'device',
  LOCATION = 'location',
  GROUPS = 'groups',
  ROLES = 'roles',
  DEVICES = 'devices',
  MDN = 'mdn',
  MIN = 'min',
  MAC_ADDRESS = 'macAddress',
  PENDING_JPOD_FW = 'pendingJpodFw',
  PENDING_JPOD_CONFIG = 'pendingJpodConfig',
  PENDING_EDGE_APP = 'pendingEdgeApp',
  JPOD_FIRMWARE = 'jpodFirmware',
  VBUS_DATABASE = 'vbusDatabase',
  JPOD_CONFIG = 'jpodConfig',
  ANDRIOD_MDT_FIRMWARE = 'androidMdtFirmware',
  BLE3_FIRMWARE = 'ble3Firmware',
  VBU3_FIRMWARE = 'vbu3Firmware',
  RADIO_FIRMWARE = 'radioFirmware',
  EDGE_APP_VERSION = 'edgeAppVersion',
  MOBILE_ID = 'mobileId',
  EXTENSIONS_APP_ID = 'extensions.appId',
  DEVICE_CONFIGURATION = 'deviceConfiguration',
  EXTENSIONS_FIRMWARE_VERSION = 'extensions.firmwareVersion',
  ICCID = 'iccid',
  IMEI = 'imei',
  IMEI2 = 'imei2',
  IMSI = 'imsi',
  ISDN = 'isdn',
  MEID = 'meid',
  PEG_BEHAVIOR = 'pegBehavior',
  VBUS_STATUS = 'vbusStatus',
  EXTENSIONS_CONFIG_VERSION = 'extensions.configVersion',
  FIRST_ID_REPORT_TIME = 'firstIdReportTime',
  FILE_HISTORY_PENDING_NEW_VERSION = 'fileHistoryPending.newVersion',
  NEW_VERSION = 'newVersion',
  ENABLED_SERVICES = 'enabledServices',
  DISABLED_SERVICES = 'disabledServices',
  ION_TAG = 'iOnTag',
  VBUS_MODE = 'vbusMode',
  MDT = 'mdt',
  LMU = 'lmu',
  SENTINEL_ASSOCIATION = 'sentinelAssociation',
  USER_ESN_MOBILE_ID = 'userESN',
  VIN = 'vin',
  TYPE = 'validationMetaData.platformName',
  FILE_VERSION = 'validationMetaData.fields.version',
  APPID = 'appId',
  STATUS = 'status',
  CREATED_ACCOUNT = 'account.title',
  FILE_STATUS = 'validationStatus.status',
  FILE_INVALID_REASON = 'validationStatus.reasons',
  DESCRIPTION = 'description',
  AUTO_JOB_ID = 'validationMetaData.autoJobId',
  ENTITY_NAME = 'entityName',
  IDENTIFIER_TYPE = 'identifierType',
  FEATURES = 'features',
  FAILED_REASON = 'failedReason',
  PROGRESS_STATE = 'progressState',
  LAST_UPDATE = 'lastUpdate',
  CONFIG_STATUS = 'configStatus',
  FIRMWARE_STATUS = 'firmwareStatus',
  JOB_ID = 'jobId',
  USER_NAME = 'username',
  TASKS = 'tasks',
  TASK_COUNT = 'taskCount',
  DEVICES_COUNT = 'devicesCount',
  PROGRESS_STATUS = 'progressStatus',
  CREATED_ON = 'createdOn',
  ERROR_CODES = 'errorCodes',
  UPDATE_TIME = 'updateTime',
  TASK = 'task',
  TASK_DESCRIPTION = 'taskDescription',
  DEVICE_COUNT = 'deviceCount',
  COMPLETED = 'completed',
  PENDING = 'pending',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  DETAILS = 'details',
  PURCHASE_ORDER = 'purchaseOrder',
  SALES_ORDER = 'salesOrder',
  TRACKING = 'trackingNumber',
  VEHICLE_EXEMPT = 'vehicleExempt',
  VEHICLE_TYPE_MOTOR_HOME = 'vehicleTypeMotorHome',
  VEHICLE_TYPE_AGRICULTUREAL = 'vehicleTypeAgricultural',
  MANUFACTURED_YEAR = 'manufacturedYear',
  TEST_DATE = 'testDate',
  TEST_TYPE = 'testType',
  TEST_RESULT = 'testResult'
};

// Available field names for the Bulk email export date format fields.
enum DATE_FORMAT_FIELDS {
  LAST_REPORTED_TIME = 'lastReportedTime',
  ACTIVATION_DATE = 'activationDate',
  FIRST_ICN = 'firstIcn',
  FIRST_ID_REPORT_TIME = 'firstIdReportTime',
  EXTENSIONS_ID_REPORT_TIMESTAMP = 'extensions.idReportTimestamp',
  LAST_AVL_COMMUNICATION_LOCATION_TIME = 'lastAvlCommunication.locationTime',
  LAST_ICN = 'lastIcn',
  LAST_MODIFIEDON = 'lastModifiedOn',
  SHIP_DATE = 'shipDate',
  CREATED_ON = 'createdOn',
  UPDATED_ON = 'updatedOn',
  UPDATE_TIME = 'updateTime',
  INSTALL_DATE = 'installDate',
  UNINSTALL_DATE = 'uninstallDate',
  ENGINE_HOURS_REFERENCE_TIME = 'engineHoursReferenceTime',
  ODOMETER_REFERENCE_TIME = 'odometerReferenceTime',
  FEE_DUE_DATE = 'feeDueDate',
  LAST_EMISSION_DATA = 'lastEmissionsData',
  NEXT_COMPLIANCE_DUE_DATE = 'nextComplianceDueDate',
  MANUFACTURING_DATE = 'manufacturingDate',
  NST_DUE_DATE = 'nstDueDate',
  SUBMISSION_DATE = 'submissionDate',
  SUBMISSION_DUE_DATE = 'submissionDueDate',
  REGISTRATION_RENEWAL_DATE = 'registrationRenewalDate',
  DATE = 'date',
  LAST_UPDATE = 'lastUpdate',
  TEST_DATE = 'testDate'
};

// Available field names for the Bulk email export custom api fields.
enum CUSTOM_API_FIELDS {
  EOL = 'eol',
  FIRST_ICN = 'firstIcn',
  LAST_ICN = 'lastIcn',
  VEHICLE_PROFILE = 'vinInfo',
  FILE_TYPES = 'fileTypes'
};

// initial form state values
const required = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the email export form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, or a
 * reference to a export, in the case of edits.
 */
export const getInitialState = () => ({
  [EmailExportFields.EmailAddress] : required
});

export const serialize = (data: FormState) => {
  const exportData = {};
  for (const [key, obj] of Object.entries(data)) {
    exportData[key] = obj.value ?? null;
  }
  return exportData;
};

export const buildQueryString = (
  viewSubGroups: boolean,
  searchFilters: GenericObject,
  accountId: number
): ExportSearch => {
  const formQuery = {accountId, queryString: '', hierLevel: ''};
  if (viewSubGroups) {
    formQuery.hierLevel = 'ALL';
  }
  if (!isEmpty(searchFilters)) {
    formQuery.queryString = createModularSearchQueryString(searchFilters);
  }
  if (!formQuery.queryString) {
    formQuery.queryString = '(id: (*))';
  }
  return {smartSearch: {search: formQuery}};
};

export const buildMemberQueryString = (
  sort: GenericObject,
  search: GenericObject
): APIGatewayMemberSearch => {
  const formQuery = {
    sort,
    searchTerms : {
      ...search?.searchCriteria?.[0]?.memberSearch?.searchTerms
    }
  };
  return {memberSearch: {search: formQuery}};
};

export const buildJobsTaskMemberQueryString = (
  accountId: number,
  jobId: number,
  taskIndex: number
): JobsTaskExportSearch => {
  const formQuery = {
    searchTerms : {
      account : accountId,
      jobId,
      taskIndex
    }
  };
  return {memberSearch: {search: formQuery}};
};

export const buildJobSearchEngineQueryString = ({
  accountId,
  queryString,
  includeSubaccounts,
  additionalProperties,
  query,
  sort
}): JobSearchEngineExportSearch => {
  const formQuery = {
    search : {
      accountId,
      queryString,
      includeSubaccounts,
      additionalProperties,
      query,
      sort
    }
  };
  return {smartSearch: formQuery};
};

export const buildCarbQueryString = ({
  esn,
  sort,
  accountId
}: CarbExportParams): CarbExportSearch => {
  const formQuery = {
    search : {
      sort,
      searchTerms : {
        ...(esn && {esn}),
        ...(accountId && {account: accountId})
      }
    }
  };
  return {memberSearch: formQuery, searchType: 'memberSearch', appendEntityIdentifier: false};
};

export const buildProvisionedDevicesQueryString = (
  search: GenericObject
): InventoryExportSearch => {
  const formQuery = {
    search
  };
  return {memberSearch: formQuery};
};

export const buildManufacturedDevicesQueryString = (search: GenericObject) => {
  const formQuery = {
    search
  };
  return {memberSearch: formQuery};
};

export const buildCarrierPartNumberQueryString = ({
  accountId,
  sortOn,
  query
}): CarrierPartNumbersExportSearch => {
  const formQuery = {
    search : {
      accountId,
      searchTerms : {
        ...query
      },
      sort : sortOn
    }
  };
  return {memberSearch: formQuery};
};

export const getSearchFilters = ({
  filters,
  fileIds,
  platformId,
  dateRange,
  nameFilter,
  query = 'jobId:*',
  isFileTypeChecked = false
}) => {
  const jobNames               = !isEmpty(nameFilter) ? nameFilter?.split(',') : [];
  const shouldUseElasticSearch = !!dateRange && dateRange.gte && dateRange.lte;
  const jobIds                 = getFilterValue(filters, 'jobId');
  const statusFilter           = getFilterValue(filters, 'progressStatus');
  const usernameFilter         = getFilterValue(filters, 'username');
  const isMaxIds               = jobIds?.length > 20;
  const hasValidPlatformId     = !isNil(platformId) && !isEmpty(platformId);
  const platformIdFilter       = hasValidPlatformId && isFileTypeChecked;

  const wildcardSearch = jobNames?.length
    ? {
      bool : {
        should : jobNames?.map((jobName) => ({
          wildcard : {
            name : {
              value            : `*${jobName}*`,
              case_insensitive : true
            }
          }
        })),
        minimum_should_match : 1
      }
    }
    : null;
    
  const nestedSearch = {
    nested : {
      path  : 'tasks',
      query : {
        bool : {
          must : [{
            term : {
              'tasks.metadata.platformId' : platformId
            }
          }]
        }
      }
    }
  };

  const elasticQuery = [
    {
      query_string : {
        query
      }
    }
  ];

  if (jobNames?.length) {
    elasticQuery.push({...wildcardSearch});
  }
  if (platformIdFilter) {
    elasticQuery.push({...nestedSearch});
  }

  if (fileIds?.length) {
    elasticQuery.push({...filesSearchQuery});
  }
  const elasticSearch = {
    bool : {
      must   : elasticQuery,
      filter : shouldUseElasticSearch
        ? [
          {
            range : {
              createdOn : {
                gte : dateRange.gte,
                lte : dateRange.lte
              }
            }
          }
        ]
        : []
    }
  };

  const standardSearch = {
    query_string : {
      query
    }
  };

  const filesSearchQuery = {
    bool : {
      must : [{
        nested : {
          path  : 'tasks',
          query : {
            bool : {
              must : [
                {
                  terms : {
                    'tasks.metadata.file' : fileIds
                  }
                }
              ]
            }
          }
        }
      }]
    }
  };

  const termsMustQuery = [];

  if (jobIds?.length) {
    termsMustQuery.push({
      terms : {
        jobId : jobIds
      }
    });
  }

  if (statusFilter?.length) {
    termsMustQuery.push({
      terms : {
        progressStatus : statusFilter
      }
    });
  }
  if (usernameFilter?.length) {
    termsMustQuery.push({
      terms : {
        username : usernameFilter
      }
    });
  }

  if (jobNames?.length) {
    termsMustQuery.push({
      bool : {
        should : jobNames?.map((jobName) => ({
          wildcard : {
            name : {
              value            : `*${jobName}*`,
              case_insensitive : true
            }
          }
        })),
        minimum_should_match : 1
      }
    });
  }

  if (fileIds?.length) {
    termsMustQuery.push(filesSearchQuery);
  }

  const termsSearch = {
    bool : {
      filter : shouldUseElasticSearch
        ? [
          {
            range : {
              createdOn : {
                gte : dateRange.gte,
                lte : dateRange.lte
              }
            }
          }
        ]
        : [],
      must : termsMustQuery
    }
  };

  if (fileIds?.length && query && !isEmpty(query) && query !== '(jobId:*)') {
    filesSearchQuery?.bool?.must.push({
      query_string : {
        query
      }
    });
  }

  return isMaxIds
    ? termsSearch
    : shouldUseElasticSearch || platformIdFilter || !!jobNames?.length
      ? elasticSearch
      : fileIds?.length
        ? filesSearchQuery
        : standardSearch;
};

const ALL_JOBS_SEARCH_CUSTOM_LOGIC_COLUMN = {
  [CUSTOM_LOGIC_FIELDS.ACCOUNT] :
    'module.exports = () => { return dataList?.account?.title }',
  [CUSTOM_LOGIC_FIELDS.TASKS] :
    'module.exports = () => { return dataList?.taskCount }',
  [CUSTOM_LOGIC_FIELDS.DEVICES] :
    'module.exports = () => { return dataList?.tasks?.[0]?.deviceCount }'
};

const JOBS_SEARCH_DATE_FORMAT_COLUMN = [DATE_FORMAT_FIELDS.UPDATE_TIME];

const JOBS_SEARCH_CUSTOM_LOGIC_COLUMN = {
  [CUSTOM_LOGIC_FIELDS.DEVICES_COUNT] :
    'module.exports = () => { return dataList?.tasks?.[0]?.deviceCount }',
  [CUSTOM_LOGIC_FIELDS.ACCOUNT] :
    'module.exports = () => { return dataList?.account?.title }',
  [CUSTOM_LOGIC_FIELDS.CREATED_ON] :
    'module.exports = () => { return dataList?.createdOn ? dataList.createdOn.split("T")[0] : "" }',
  [CUSTOM_LOGIC_FIELDS.IDENTIFIER] :
    'module.exports = () => { return dataList?.device?.esn || dataList?.device?.serialNumber }',
  [CUSTOM_LOGIC_FIELDS.STATUS] :
    'module.exports = () => { return dataList?.taskStatus?.status }',
  [CUSTOM_LOGIC_FIELDS.ERROR_CODES] :
    'module.exports = () => { return dataList?.taskStatus?.errorCodes[0] }',
  [CUSTOM_LOGIC_FIELDS.UPDATE_TIME] :
    'module.exports = () => { return dataList?.taskStatus?.updateTime }',
  [CUSTOM_LOGIC_FIELDS.TASK] :
    'module.exports = () => { return dataList?.taskIndex ? "Task " + (dataList.taskIndex + 1) : "Task " + (dataList.taskIndex + 1) }',
  [CUSTOM_LOGIC_FIELDS.TASK_DESCRIPTION] :
    'module.exports = () => { return dataList?.metadata?.title || dataList?.description }',
  [CUSTOM_LOGIC_FIELDS.COMPLETED] :
    'module.exports = () => { return dataList?.counts?.completed }',
  [CUSTOM_LOGIC_FIELDS.PENDING] :
    'module.exports = () => { return dataList?.counts?.pending }',
  [CUSTOM_LOGIC_FIELDS.FAILED] :
    'module.exports = () => { return dataList?.counts?.failed }',
  [CUSTOM_LOGIC_FIELDS.CANCELLED] :
    'module.exports = () => { return dataList?.counts?.cancelled }'
};

const JOBS_CUSTOM_LOGIC_COLUMN = {
  [CUSTOM_LOGIC_FIELDS.IDENTIFIER] :
    'module.exports = () => { const MDTCategories = { MDT: "MDT", CAMERA: "Camera" }; const isMDTDevice = (device) => { const category = device?.category; if (category) { return !!MDTCategories[(Array.isArray(category) ? category?.[0] : category)]; } return false; }; const getDeviceIdentifierFromType = (device) => { let identifier; if (isMDTDevice(device)) { identifier = device?.serialNumber; } else { identifier = device?.esn; } return identifier || ""; }; return getDeviceIdentifierFromType(dataList); }',
  [CUSTOM_LOGIC_FIELDS.FAILED_REASON] :
    'module.exports = () => { const statCode = ["UNKNOWN", "Download in Progress", "Download Complete", "Download Failed - Retrying", "Programming in Progress", "Programming Complete", "Programming Failed"]; const diagCode = ["None", "Invalid file length", "Invalid file", "Flash erase in-progress", "Flash erase complete", "Flash erase failed", "OTA field error", "Writing file to flash", "HTTP open failed", "HTTP premature termination", "Flash verify failed", "Flash write failed", "Download time-out", "CRC check failed", "User-aborted download", "Version incompatible", "Invalid URL", "Invalid directory", "HTTP connect failed", "HTTP protocol error", "File IO error", "Activity error", "Unknown file", "Failsafe recovery", "Bad digital signature", "Download prohibited", "Device unresponsive", "Digital signature required", "FOTA download failed", "FOTA upgrade failed", "Geo-zone process not up", "Geo-zone invalid parameter", "Geo-zone file invalid", "Geo-zone file empty", "Geo-zone file too big", "Geo-zone file version", "Geo-zone error", "Download failure", "Net download internal error", "Resolve failure", "Net transfer failure"]; const diagCode32Bit = {"29": "same from here and up", "30": "No VLU box number", "31": "Unsafe upgrade", "32": "No digital signature key", "33": "unused from here and down"}; const extractValues = (errorCode) => { return errorCode?.match(/\\d+/g)?.map(Number) || "";  }; const getStatCodeDescription = (errorCode, statCode) => { const code = extractValues(errorCode); return statCode[code[0]]; }; const getDiagCodeDescription = (errorCode, appId, diagCode, diagCode32Bit) => { const code = extractValues(errorCode); const error = code[1]; if (appId < 1000 && (error === 29 || error === 30 || error === 31 || error === 32 || error === 33)) { return diagCode32Bit[error]; } return diagCode[error]; }; const getDiagMessage = (diagMsg) => { const message = diagMsg?.match(/DiagMsg:\s*(.+)/); return message?.[1]; }; return dataList?.taskStatus?.errorCodes?.[0]?.indexOf("DiagCode") > -1 ? "StatCode: " + getStatCodeDescription(dataList?.taskStatus?.errorCodes?.[0], statCode) + ", DiagCode: " + getDiagCodeDescription(dataList?.taskStatus?.errorCodes?.[0], dataList?.device?.extensions?.appId, diagCode, diagCode32Bit) + ", DiagMsg: " + getDiagMessage(dataList?.taskStatus?.errorCodes?.[0]) : dataList?.taskStatus?.errorCodes?.[0] }',
  [CUSTOM_LOGIC_FIELDS.PROGRESS_STATE] :
    'module.exports = () => { return dataList?.taskStatus?.status }',
  [CUSTOM_LOGIC_FIELDS.LAST_UPDATE] :
    'module.exports = () => { return dataList?.taskStatus?.updateTime || "N/A" }'
};

const CUSTOM_LOGIC_COLUMN = (
  isVbuFwEnabled: boolean = false,
  isAssetExport: boolean = false,
  isCarbExportList: boolean = false
) => ({
  [CUSTOM_LOGIC_FIELDS.IDENTIFIER] :
    'module.exports = () => { return dataList?.esn || dataList?.serialNumber || dataList?.iccid || dataList?.meid || dataList?.imei || dataList?.imei2 || dataList?.imsi || dataList?.mdn || dataList?.externalId || dataList?.ipAddress || dataList?.id }',
  [CUSTOM_LOGIC_FIELDS.ACCOUNT] :
    'module.exports = () => { return dataList?.account?.title }',
  [CUSTOM_LOGIC_FIELDS.ASSET] :
    'module.exports = () => { return dataList?.asset?.title }',
  [CUSTOM_LOGIC_FIELDS.DEVICE] :
    'module.exports = () => { const href = dataList?.device?.href || \'\'; return dataList?.device?.title || href.slice(href.lastIndexOf(\'/\') + 1) }',
  [CUSTOM_LOGIC_FIELDS.LOCATION] :
    'module.exports = () => { return dataList?.address?.label }',
  [CUSTOM_LOGIC_FIELDS.GROUPS] :
    'module.exports = () => { return dataList?.groups?.map(item => { return item.title })?.join(",") || null }',
  [CUSTOM_LOGIC_FIELDS.ROLES] :
    'module.exports = () => { return dataList?.roles?.map(item => { return item.title})?.join(",") || null }',
  [CUSTOM_LOGIC_FIELDS.DEVICES] :
    'module.exports = () => { return dataList?.devices?.map(item => { return item.title})?.join(",") || null }',
  [CUSTOM_LOGIC_FIELDS.MDN] :
    'module.exports = () => { return dataList?.mdn || null }',
  [CUSTOM_LOGIC_FIELDS.MIN] :
    'module.exports = () => { return dataList?.min || dataList?.idReport?.min || null }',
  [CUSTOM_LOGIC_FIELDS.MAC_ADDRESS] :
    'module.exports = () => { return dataList?.macAddress || null }',
  [CUSTOM_LOGIC_FIELDS.PENDING_JPOD_FW] : isVbuFwEnabled
    ? 'module.exports = () => { let isJPOD; let isOBD; let isVBU; const deviceMode = dataList?.extensions?.vbusMode; if (deviceMode) { isJPOD = deviceMode === "JPOD"; isOBD = deviceMode === "OBD"; isVBU = deviceMode === "VBU"; } return dataList?.fileHistoryPending?.filter(({ platformId }) => isJPOD ? platformId === 374 || platformId === 399 : isOBD ?  platformId === 393 || platformId === 377 : isVBU ? platformId === 419 : "")?.map?.(result => "["+result.newVersion+"] "+result.jobId)?.join(",") || "" }'
    : 'module.exports = () => { return dataList?.fileHistoryPending?.filter(({ platformId }) => platformId === 374 || platformId === 399)?.map?.(result => "["+result.newVersion+"] "+result.jobId)?.join(",") || "" }',
  [CUSTOM_LOGIC_FIELDS.PENDING_JPOD_CONFIG] : isVbuFwEnabled
    ? 'module.exports = () => { let isJPOD; const deviceMode = dataList?.extensions?.vbusMode; if (deviceMode) { isJPOD = deviceMode === "JPOD"; } return dataList?.fileHistoryPending?.filter(({platformId}) => isJPOD ? platformId === 375 || platformId === 761 : "")?.map?.(result => "["+result.newVersion+"] "+result.jobId)?.join(",") || "" }'
    : 'module.exports = () => { return dataList?.fileHistoryPending?.filter(({platformId}) => platformId === 375 || platformId === 761)?.map?.(result => "["+result.newVersion+"] "+result.jobId)?.join(",") || "" }',
  [CUSTOM_LOGIC_FIELDS.PENDING_EDGE_APP] :
    'module.exports = () => { return dataList?.fileHistoryPending?.filter(({platformId}) => platformId === 381 || platformId === 382 || platformId === 387 || platformId === 388 || platformId === 389 || platformId === 390 || platformId === 391 || platformId === 392 || platformId === 383 || platformId === 384 || platformId === 385 || platformId === 386 || platformId === 825)?.map?.(result => "["+result.newVersion+"] "+result.jobId)?.join(",") || "" }',
  [CUSTOM_LOGIC_FIELDS.JPOD_FIRMWARE] : isVbuFwEnabled
    ? 'module.exports = () => { let isJPOD; let isOBD; let isVBU; const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { const deviceMode = dataList?.extensions?.vbusMode; if (deviceMode) { isJPOD = deviceMode === "JPOD"; isOBD = deviceMode === "OBD"; isVBU = deviceMode === "VBU"; } return dataList?.extensions?.fileVersions?.filter(({ platform }) => isJPOD ? platform === 374 || platform === 399 : isOBD ?  platform === 393 || platform === 377 : isVBU ? platform === 419 : "")?.map?.((result) => result.version )?.join(",") || ""; } }'
    : 'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 374 || platform === 399)?.map?.((result) => result.version )?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.VBUS_DATABASE] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 394)?.map?.((result) => result.version )?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.JPOD_CONFIG] : isVbuFwEnabled
    ? 'module.exports = () => { let isJPOD; const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { const deviceMode = dataList?.extensions?.vbusMode; if (deviceMode) { isJPOD = deviceMode === "JPOD"; } return dataList?.extensions?.fileVersions?.filter(({platform}) => isJPOD ? platform === 375 || platform === 761 : "")?.map?.(result => result.version)?.join(",") || ""; } }'
    : 'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({platform}) => platform === 375 || platform === 761)?.map?.(result => result.version)?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.ANDRIOD_MDT_FIRMWARE] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 396)?.map?.(result => result.version)?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.BLE3_FIRMWARE] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 420)?.map?.(result => result.version)?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.VBU3_FIRMWARE] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 419)?.map?.(result => result.version)?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.RADIO_FIRMWARE] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 767 || platform === 405)?.map?.(result => result.version)?.join(",") || ""; } }',
  [CUSTOM_LOGIC_FIELDS.EDGE_APP_VERSION] :
    'module.exports = () => { const fileVersions = dataList?.extensions?.fileVersions; if (!fileVersions || (!Array.isArray(fileVersions))) { return ""; } if (fileVersions) { return dataList?.extensions?.fileVersions?.filter(({ platform }) => platform === 825 || platform === 381 || platform === 387 || platform === 389 || platform === 390 || platform === 391 || platform === 392)?.map?.(result => result.version)?.join(",") || "";}}',
  [CUSTOM_LOGIC_FIELDS.CONFIG_STATUS] :
    'module.exports = () => { return dataList?.jobFileStatus?.filter(({filePlatformId}) => (filePlatformId === 366 || filePlatformId === 373))?.map(result => result.transactionStatus) || "";}',
  [CUSTOM_LOGIC_FIELDS.FIRMWARE_STATUS] :
    'module.exports = () => { return dataList?.jobFileStatus?.filter(({filePlatformId}) => filePlatformId === 365)?.map(result => result.transactionStatus) || "";}',
  [CUSTOM_LOGIC_FIELDS.MOBILE_ID] :
    'module.exports = () => { return dataList?.userDefinedMobileId || null }',
  [CUSTOM_LOGIC_FIELDS.EXTENSIONS_APP_ID] :
    'module.exports = () => { return dataList?.extensions?.appId || dataList?.idReport?.appId || null }',
  [CUSTOM_LOGIC_FIELDS.DEVICE_CONFIGURATION] :
    'module.exports = () => { return dataList?.deviceConfiguration?.title || null }',
  [CUSTOM_LOGIC_FIELDS.EXTENSIONS_FIRMWARE_VERSION] :
    'module.exports = () => { return dataList?.extensions?.firmwareVersion || dataList?.idReport?.firmwareVersion || null }',
  [CUSTOM_LOGIC_FIELDS.ICCID] :
    'module.exports = () => { return dataList?.iccid || dataList?.idReport?.iccid || dataList?.secondaryIdents?.iccid || null }',
  [CUSTOM_LOGIC_FIELDS.IMEI] :
    'module.exports = () => { return dataList?.imei || dataList?.idReport?.imei || dataList?.secondaryIdents?.imei || null }',
  [CUSTOM_LOGIC_FIELDS.IMEI2] :
    'module.exports = () => { return dataList?.imei2 || dataList?.idReport?.imei2 || null }',
  [CUSTOM_LOGIC_FIELDS.IMSI] :
    'module.exports = () => { return dataList?.imsi || dataList?.idReport?.imsi || dataList?.secondaryIdents?.imsi || null }',
  [CUSTOM_LOGIC_FIELDS.ISDN] :
    'module.exports = () => { return dataList?.isdn || dataList?.idReport?.isdn || null }',
  [CUSTOM_LOGIC_FIELDS.MEID] :
    'module.exports = () => { return dataList?.meid || dataList?.idReport?.meid || null }',
  [CUSTOM_LOGIC_FIELDS.PEG_BEHAVIOR] :
    'module.exports = () => { return dataList?.pegBehavior?.title || null }',
  [CUSTOM_LOGIC_FIELDS.VBUS_STATUS] :
    'module.exports = () => { const deviceStatus = dataList?.extensions?.vbusEnabledStatus; const status = typeof deviceStatus == "string" ? JSON.parse(deviceStatus) : deviceStatus; if (status === undefined) { return ""; } if (status) { return "ON"; } return "OFF"; }',
  [CUSTOM_LOGIC_FIELDS.EXTENSIONS_CONFIG_VERSION] :
    'module.exports = () => { return dataList?.extensions?.configVersion || null }',
  [CUSTOM_LOGIC_FIELDS.FIRST_ID_REPORT_TIME] :
    'module.exports = () => { return dataList?.idReport?.firstIdReportTime || null }',
  [CUSTOM_LOGIC_FIELDS.FILE_HISTORY_PENDING_NEW_VERSION] :
    'module.exports = () => { const { fileHistoryPending = [] } = dataList; const filterPlatformIds = []; fileHistoryPending.map((item) => { if (item.platformId === 365) { const jobId = item && item.jobId ? item.jobId : ""; const newVersion = item && item.newVersion ? item.newVersion : ""; filterPlatformIds.push(jobId ? "["+newVersion+"] "+jobId : "["+newVersion+"]"); } return true; }); return filterPlatformIds.length > 0 ? filterPlatformIds.join() : ""; }',
  [CUSTOM_LOGIC_FIELDS.NEW_VERSION] :
    'module.exports = () => { const { fileHistoryPending = [] } = dataList; const filterPlatformIds = []; fileHistoryPending.map((item) => { if (item.platformId === 366 || item.platformId === 373) { const jobId = item && item.jobId ? item.jobId : ""; const newVersion = item && item.newVersion ? item.newVersion : ""; filterPlatformIds.push(jobId ? "["+newVersion+"] "+jobId : "["+newVersion+"]"); } return true; }); return filterPlatformIds.length > 0 ? filterPlatformIds.join() : ""; }',
  [CUSTOM_LOGIC_FIELDS.ENABLED_SERVICES] :
    'module.exports = () => { const { features } = dataList || {}; const enabledServicesList = []; if (typeof features === "object" && features !== null && !Array.isArray(features)) { for (const [key, value] of Object.entries(features)) { if (value === "Enabled") { enabledServicesList.push(key); } if (value === "PendingEnable") {  enabledServicesList.push(key+"*"); } } } return enabledServicesList.length > 0 ? enabledServicesList.join(",") : ""; }',
  [CUSTOM_LOGIC_FIELDS.DISABLED_SERVICES] :
    'module.exports = () => { const { features } = dataList || {}; const disabledServicesList = []; if (typeof features === "object" && features !== null && !Array.isArray(features)) { for (const [key, value] of Object.entries(features)) { if (value === "Disabled") { disabledServicesList.push(key); } if (value === "PendingDisable") {  disabledServicesList.push(key+"*"); } } } return disabledServicesList.length > 0 ? disabledServicesList.join(",") : ""; }',
  [CUSTOM_LOGIC_FIELDS.ION_TAG] :
    'module.exports = () => { if (dataList && dataList.architecture === "SCI") { const { title } = dataList.deviceConfiguration || {}; if (title.startsWith("PTA")) { return "iOnProximityTag"; } else if (title.endsWith("HT")) { return "iOnEnvironmentalTag"; } else if (title.endsWith("XT")) { return "iOnXtremeTempTag"; } else { return "Yes"; } } return "No"; }',
  [CUSTOM_LOGIC_FIELDS.VBUS_MODE] :
    'module.exports = () => { return dataList?.extensions?.vbusMode || null }',
  [CUSTOM_LOGIC_FIELDS.MDT] :
    'module.exports = () => { const LMUCategories = { LMU : "LMU", LMU_TTU : "LMU_TTU" }; const category = dataList?.category; if (category) { const lmuCategories = !!LMUCategories[(Array.isArray(category) ? category?.[0] : category)]; if (lmuCategories) { let mobileId = ""; dataList?.extensions?.associatedDevices?.forEach?.(associatedDevice => { mobileId = associatedDevice?.mobileId; }); return mobileId; } return ""; } return ""; }',
  [CUSTOM_LOGIC_FIELDS.LMU] :
    'module.exports = () => { const MDTCategories = { MDT : "MDT", CAMERA : "CAMERA" }; const category = dataList?.category; if (category) { const mdtCategories = !!MDTCategories[(Array.isArray(category) ? category?.[0] : category)]; if (mdtCategories) { let mobileId = ""; dataList?.extensions?.associatedDevices?.forEach?.(associatedDevice => { mobileId = associatedDevice?.mobileId; }); return mobileId; } return ""; } return ""; }',
  [CUSTOM_LOGIC_FIELDS.SENTINEL_ASSOCIATION] :
    'module.exports = () => { let mobileId = ""; dataList?.extensions?.associatedDevices?.forEach?.(associatedDevice => { if (associatedDevice?.tags?.[0] === "ddsar-secondary" || associatedDevice?.tags?.[0] === "ddsar-primary") { mobileId = associatedDevice?.mobileId; } }); return mobileId;}',
  [CUSTOM_LOGIC_FIELDS.USER_ESN_MOBILE_ID] :
    'module.exports = () => { return dataList?.userESN || null }',
  ...(!isAssetExport && {
    [CUSTOM_LOGIC_FIELDS.VIN] :
      'module.exports = () => { let vinData = (dataList && dataList.idReport && dataList.idReport.device && dataList.idReport.device.idReport && dataList.idReport.device.idReport.extensions && dataList.idReport.device.idReport.extensions[\'VIN-INFO\'] && dataList.idReport.device.idReport.extensions[\'VIN-INFO\'][0]) || null; if (vinData) { const parseValueWithKey = ({source, key, endingDelimiter, customValueLength = 10,}) => {if (source) {try {const keyStart = source.indexOf(key); if (keyStart > -1) { const valueStart = keyStart + key.length;const valueEnd = source.indexOf(endingDelimiter, valueStart); const resultingValueEnd = valueEnd > -1 ? valueEnd : valueStart + customValueLength;return source.slice(valueStart, resultingValueEnd);}return source;} catch (error) {error(`There is an issue parsing the value of the key. Please check your data.`);}}return \'\';}; vinData = parseValueWithKey({source: vinData, key: \'VIN=\', endingDelimiter: \',\', customValueLength: 17,});return vinData;} else if (dataList && dataList?.vin) {return dataList?.vin || null;} else {return \'\';}}'
  }),
  [CUSTOM_LOGIC_FIELDS.TYPE] :
    'module.exports = () => { return dataList?.validationMetaData?.platformName }',
  [CUSTOM_LOGIC_FIELDS.FILE_VERSION] :
    'module.exports = () => { return dataList?.validationMetaData?.fields?.version }',
  [CUSTOM_LOGIC_FIELDS.APPID] :
    'module.exports = () => { return Array.isArray(dataList?.validationMetaData?.fields?.appId) ? dataList.validationMetaData.fields.appId.map((item) => item.value)?.join(", ") : dataList?.validationMetaData?.fields?.appId ? dataList?.validationMetaData?.fields?.appId : "" }',
  [CUSTOM_LOGIC_FIELDS.CREATED_ACCOUNT] :
    'module.exports = () => { return dataList?.account?.title }',
  [CUSTOM_LOGIC_FIELDS.FILE_STATUS] :
    'module.exports = () => { return dataList?.validationStatus?.status }',
  [CUSTOM_LOGIC_FIELDS.FILE_INVALID_REASON] :
    'module.exports = () => { return dataList?.validationStatus?.reasons?.map(item => { return item?.code + " - " + item?.message})?.join(", ") || null }',
  [CUSTOM_LOGIC_FIELDS.AUTO_JOB_ID] :
    'module.exports = () => { return dataList?.validationMetaData?.autoJobId }',
  [CUSTOM_LOGIC_FIELDS.ENTITY_NAME] :
    'module.exports = () => { return dataList?.name || dataList?.identifier || dataList?.entityType || dataList?.firstName + " - " + dataList?.lastName }',
  [CUSTOM_LOGIC_FIELDS.IDENTIFIER_TYPE] :
    'module.exports = () => { let identifierType = "ID"; if (dataList?.esn) { identifierType = "ESN";} else if (dataList?.serialNumber) { identifierType = "Serial Number";} else if (dataList?.iccid) { identifierType = "ICCID"; } else if (dataList?.meid) { identifierType = "MEID"; } else if (dataList?.imei) {identifierType = "IMEI";} else if (dataList?.imei2) {identifierType = "IMEI2";} else if (dataList?.imsi) { identifierType = "IMSI"; } else if (dataList?.mdn) { identifierType = "MDN"; } else if (dataList?.externalId) { identifierType = "External ID";} else if (dataList?.ipAddress) { identifierType = "Ip Address"; } else if (dataList?.deviceId) { identifierType = "ID";} else if (dataList?.name) { identifierType = "Name"; } else if (dataList?.deviceTypeName) { identifierType = "Device Type Name"; } return identifierType; }',
  [CUSTOM_LOGIC_FIELDS.FEATURES] :
    'module.exports = () => { return (dataList?.features && Object.keys(dataList?.features).map(key => key + " - " + dataList?.features[key]).join(", ")) || "" }',
  [CUSTOM_LOGIC_FIELDS.PURCHASE_ORDER] :
    'module.exports = () => { return dataList?.externalData?.purchaseOrder || null }',
  [CUSTOM_LOGIC_FIELDS.SALES_ORDER] :
    'module.exports = () => { return dataList?.externalData?.salesOrder || null }',
  [CUSTOM_LOGIC_FIELDS.TRACKING] :
    'module.exports = () => { return dataList?.externalData?.trackingNumber || null }',
  [CUSTOM_LOGIC_FIELDS.VEHICLE_EXEMPT] :
    'module.exports = () => { if (dataList && dataList?.vehicleExempt) { return "Y"; } else { return "N"; } }',
  [CUSTOM_LOGIC_FIELDS.VEHICLE_TYPE_MOTOR_HOME] :
    'module.exports = () => { if (dataList && dataList?.vehicleTypeMotorHome) { return "Y"; } else { return "N"; } }',
  [CUSTOM_LOGIC_FIELDS.VEHICLE_TYPE_AGRICULTUREAL] :
    'module.exports = () => { if (dataList && dataList?.vehicleTypeAgricultural) { return "Y"; } else { return "N"; } }',
  [CUSTOM_LOGIC_FIELDS.MANUFACTURED_YEAR] :
    'module.exports = () => { return dataList?.year || null }',
  [CUSTOM_LOGIC_FIELDS.TEST_TYPE]   : 'module.exports = () => { return dataList?.testType || null }',
  [CUSTOM_LOGIC_FIELDS.TEST_RESULT] : 'module.exports = () => { const result = dataList?.testResult; if(result === "PASS") {return "Compliant";} else if (result === "FAIL") { return "Non-Compliant";} else {return result;}}'
});

const DATE_FORMAT_COLUMN = [
  DATE_FORMAT_FIELDS.LAST_REPORTED_TIME,
  DATE_FORMAT_FIELDS.ACTIVATION_DATE,
  DATE_FORMAT_FIELDS.FIRST_ICN,
  DATE_FORMAT_FIELDS.FIRST_ID_REPORT_TIME,
  DATE_FORMAT_FIELDS.EXTENSIONS_ID_REPORT_TIMESTAMP,
  DATE_FORMAT_FIELDS.LAST_AVL_COMMUNICATION_LOCATION_TIME,
  DATE_FORMAT_FIELDS.LAST_ICN,
  DATE_FORMAT_FIELDS.LAST_MODIFIEDON,
  DATE_FORMAT_FIELDS.SHIP_DATE,
  DATE_FORMAT_FIELDS.CREATED_ON,
  DATE_FORMAT_FIELDS.UPDATED_ON,
  DATE_FORMAT_FIELDS.UPDATE_TIME,
  DATE_FORMAT_FIELDS.INSTALL_DATE,
  DATE_FORMAT_FIELDS.UNINSTALL_DATE,
  DATE_FORMAT_FIELDS.ENGINE_HOURS_REFERENCE_TIME,
  DATE_FORMAT_FIELDS.ODOMETER_REFERENCE_TIME,
  DATE_FORMAT_FIELDS.FEE_DUE_DATE,
  DATE_FORMAT_FIELDS.LAST_EMISSION_DATA,
  DATE_FORMAT_FIELDS.NEXT_COMPLIANCE_DUE_DATE,
  DATE_FORMAT_FIELDS.MANUFACTURING_DATE,
  DATE_FORMAT_FIELDS.NST_DUE_DATE,
  DATE_FORMAT_FIELDS.SUBMISSION_DATE,
  DATE_FORMAT_FIELDS.SUBMISSION_DUE_DATE,
  DATE_FORMAT_FIELDS.REGISTRATION_RENEWAL_DATE,
  DATE_FORMAT_FIELDS.DATE,
  DATE_FORMAT_FIELDS.LAST_UPDATE,
  DATE_FORMAT_FIELDS.TEST_DATE
];

const CUSTOM_API_COLUMN = (accountId, viewSubGroups = false) => ({
  [CUSTOM_API_FIELDS.EOL] : {
    apiurl        : url('/platforms/types/app_id', Service.PulsBaseUrl),
    requestMethod : 'Get',
    customLogic :
      'module.exports = () => { const appId = Number(dataList && dataList.extensions && dataList.extensions.appId) || null;let eolFlag = false;if (appId && customAPIresult.length) { const platformObj = customAPIresult.find(item => Number(item.value) === appId); if (platformObj) {const eolObj = platformObj && platformObj.platformParamValues.find(({ platformTypeParam }) => platformTypeParam && platformTypeParam.name === "supported");eolFlag = eolObj && eolObj.value && eolObj.value.toLowerCase().toString() === "false";}} return eolFlag ? "Yes" : "No"; }'
  },
  [CUSTOM_API_FIELDS.FIRST_ICN] : {
    apiurl : url(
      `/devices/search?hierarchy=${!!viewSubGroups}`,
      Service.Crashboxx
    ),
    requestMethod  : 'Post',
    requestPayload : {
      devicecheckinsearch : {
        accountId,
        deviceIdList  : '[|{|esn|}|]',
        accountIdType : 'AccountType',
        deviceIdType  : 'ESN'
      }
    },
    customLogic :
      'module.exports = () => { const { response } = customAPIresult || {}; const { results } = response || {}; const { devicecheckinmodel } = results || {}; const { devicecheckindata } = devicecheckinmodel || []; const filterResult = devicecheckindata && devicecheckindata.filter(deviceList => deviceList.deviceId === dataList.esn); return (filterResult && filterResult.length > 0 && filterResult[0].firstHeartBeatDateTime) || null; }'
  },
  [CUSTOM_API_FIELDS.LAST_ICN] : {
    apiurl : url(
      `/devices/search?hierarchy=${!!viewSubGroups}`,
      Service.Crashboxx
    ),
    requestMethod  : 'Post',
    requestPayload : {
      devicecheckinsearch : {
        accountId,
        deviceIdList  : '[|{|esn|}|]',
        accountIdType : 'AccountType',
        deviceIdType  : 'ESN'
      }
    },
    customLogic :
      'module.exports = () => { const { response } = customAPIresult || {}; const { results } = response || {}; const { devicecheckinmodel } = results || {}; const { devicecheckindata } = devicecheckinmodel || []; const filterResult = devicecheckindata && devicecheckindata.filter(deviceList => deviceList.deviceId === dataList.esn); return (filterResult && filterResult.length > 0 && filterResult[0].lastHeartBeatDateTime) || null; }'
  },
  [CUSTOM_API_FIELDS.VEHICLE_PROFILE] : {
    apiurl             : url('/vins/test', Service.VehicleServiceUrl),
    requestMethod      : 'Get',
    queryStringAppend  : true,
    queryParameterList : [
      {
        queryStringKeyPath  : 'vin',
        queryStringKeyName  : 'vin',
        queryStringJoinData : '&vin='
      }
    ],
    customLogic : `module.exports = () => {
      let vinData = (dataList &&
        dataList.idReport &&
        dataList.idReport.device &&
        dataList.idReport.device.idReport &&
        dataList.idReport.device.idReport.extensions &&
        dataList.idReport.device.idReport.extensions['VIN-INFO'] &&
        dataList.idReport.device.idReport.extensions['VIN-INFO'][0]) || null;
        if (vinData) {
          const parseValueWithKey = ({source, key, endingDelimiter, customValueLength = 10,}) => {
            if (source) {
              try {
                const keyStart = source.indexOf(key);
                if (keyStart > -1) {
                  const valueStart = keyStart + key.length;
                  const valueEnd = source.indexOf(endingDelimiter, valueStart);
                  const resultingValueEnd = valueEnd > -1 ? valueEnd : valueStart + customValueLength;
                  return source.slice(valueStart, resultingValueEnd);
                }return source;
              } catch (error) {
                error('There is an issue parsing the value of the key. Please check your data.');
              }
            }
            return '';
          };
          vinData = parseValueWithKey({source: vinData, key: 'VIN=', endingDelimiter: ',', customValueLength: 17,});
          if (vinData) {
            const { response } = customAPIresult || {};
            const { results, errors = [] } = response || {};
            const filterResult = results &&
            results.filter(vinTestData => vinTestData.vin === vinData);
            let getVinValue;
            if (filterResult.length !== 0) {
              const { isTestVin } = filterResult && filterResult.length > 0 && filterResult[0] || {};
              if (typeof isTestVin !== 'undefined') {
                getVinValue = isTestVin ? 'Test' : 'Production';
              }
            } else {
              const newError = errors.filter((errorObject) => errorObject.error.vin === vinData);
              if (newError.length > 0) {
                getVinValue = 'Production'
              }
            }
            return getVinValue;
          } else {
            return false;
          }
        } else {return null;}
      }`
  }
});

/**
 * Utility to send all custom logic we have specified in the constructGridRows.
 */
export const getCustomLogicFields = ({
  columns = {},
  isDeviceTask = false,
  isCarbExportList = false,
  isAssetExport = false,
  isVbuFwEnabled = false
}) => {
  const customLogicFields = {};
  const customLogicObject = CUSTOM_LOGIC_COLUMN(isVbuFwEnabled, isAssetExport, isCarbExportList);
  Object.keys(columns).map((name) => {
    if (isDeviceTask && !isUndefined(JOBS_CUSTOM_LOGIC_COLUMN[name])) {
      customLogicFields[name] = JOBS_CUSTOM_LOGIC_COLUMN[name];
    } else if (!isUndefined(customLogicObject[name])) {
      customLogicFields[name] = customLogicObject[name];
    }
  });
  return customLogicFields;
};

/**
 * Utility to send all date fields we have specified in the constructGridRows.
 */
export const getDateFormatFields = (columns) => {
  const dateFormatFields = [];
  Object.keys(columns).map((name) => {
    if (DATE_FORMAT_COLUMN.includes(name)) {
      dateFormatFields.push(name);
    }
  });
  return dateFormatFields;
};

/**
 * Utility to send all custom api we have specified in the constructGridRows.
 */
export const getCustomAPIFields = (
  columns,
  accountId,
  viewSubGroups = false
) => {
  const customAPIFields = {};
  Object.keys(columns).map((name) => {
    if (!isUndefined(CUSTOM_API_COLUMN(accountId, viewSubGroups)[name])) {
      customAPIFields[name] = CUSTOM_API_COLUMN(accountId, viewSubGroups)[name];
    }
  });
  return customAPIFields;
};

/**
 * Utility to send job search custom logic we have specified in the constructGridRows.
 */
export const getJobSearchCustomLogicFields = (
  columns,
  isIndividualJob = false
) => {
  if (isIndividualJob) {
    return JOBS_SEARCH_CUSTOM_LOGIC_COLUMN;
  }

  const customLogicFields = {};
  Object.keys(columns).map((name) => {
    if (!isUndefined(ALL_JOBS_SEARCH_CUSTOM_LOGIC_COLUMN[name])) {
      customLogicFields[name] = ALL_JOBS_SEARCH_CUSTOM_LOGIC_COLUMN[name];
    }
  });
  return customLogicFields;
};

/**
 * Utility to send job search custom logic we have specified in the constructGridRows.
 */
export const getJobSearchCustomAPIFields = () => ({
  [CUSTOM_API_FIELDS.FILE_TYPES] : {
    apiurl        : url('/platform/deviceTypes', Service.PulsBaseUrl),
    requestMethod : 'Get',
    customLogic :
      'module.exports = () => { const filteredTasks = dataList?.tasks?.filter((task, index, self) => { const platformId = task?.metadata?.platformId; return self?.findIndex(item => item?.metadata?.platformId === platformId) === index }); if (customAPIresult?.length) { const fileTypes = customAPIresult?.flatMap(type => type?.fileTypes || []); return filteredTasks?.map(task => { const platformId = task?.metadata?.platformId; const deviceTypeId = fileTypes?.find(type => type?.platformId === platformId); return deviceTypeId?.platformName || [] })?.join(","); } }'
  }
});

/**
 * Utility to send job search date fields we have specified in the constructGridRows.
 */
export const getJobSearchDateFormatFields = (
  columns,
  isIndividualJob = false
) => {
  if (isIndividualJob) {
    return JOBS_SEARCH_DATE_FORMAT_COLUMN;
  }

  return getDateFormatFields(columns);
};
